/**
 * preview-state — 用共享 stage-player 引擎驱动的预览控制器（纯逻辑，node 可测）
 *
 * 把 `/compile` 产出的 Episode.steps 用 **同一套共享 reducer**（桌面 IDE 预览也用它）
 * 走成一拍拍 slot `StageState`。这样顺序/分支/站位与成品完全一致（preview-parity 的
 * 核心层共享）。视觉皮肤是 lunaria-web 自己的；引擎 + 渲染器（PlayerStage）都是共享的。
 *
 * 这里**直接镜像** ls-preview 的 store（packages/ls-preview/webview/src/store/index.ts）：
 * 一个长生命周期的 `StepPlayer` + 一个 `ReduceCtx`（epoch 计数单调，全程只能有一个），
 * 每拍 `advanceUntilHalt` → 更新 stage + halt；choice/cg_show halt 映射成 `PlayerDecision`，
 * 分支下潜走 `optionEntryCursor`，cg 继续走 `resumeAfterDecisionCursor`。
 *
 * lunaria-web 暂时没有主角概念，`createReduceCtx(null)`（forcedSlotFor 在 null 时保留
 * 脚本归一化位置）。
 *
 * `BeatView` 的旧文本 API 仍然保留（其它代码/测试可能依赖），但 slot `stage()` 是渲染的
 * 新事实来源。
 */

import {
  EMPTY_STAGE,
  StepPlayer,
  advanceUntilHalt,
  createReduceCtx,
  optionEntryCursor,
  resumeAfterDecisionCursor,
  type ChoiceOptionView,
  type CursorPath,
  type EpisodeShape,
  type PlayerDecision,
  type ReduceCtx,
  type StageState,
} from "@lunaverse-ide/shared/stage-player";

export interface PreviewOption {
  readonly id: string;
  readonly label: string;
}

export interface BeatView {
  readonly kind: "line" | "narration" | "stage" | "decision" | "end";
  readonly speaker?: string;
  readonly text?: string;
  readonly note?: string;
  readonly options?: readonly PreviewOption[];
}

interface CompiledLike {
  readonly episode_id?: unknown;
  readonly steps?: unknown;
  readonly ending?: unknown;
  readonly gate?: unknown;
}

function toEpisode(output: CompiledLike | null | undefined): EpisodeShape {
  const o = output ?? {};
  const steps = Array.isArray(o.steps) ? (o.steps as unknown[]) : [];
  return {
    episode_id: typeof o.episode_id === "string" ? o.episode_id : "preview",
    steps,
    ending: (o.ending ?? null) as EpisodeShape["ending"],
    gate: o.gate ?? null,
  };
}

function str(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

// ─────────────────────────────────────────────────────────────
// halt → PlayerDecision projection (mirrors ls-preview store's
// projectChoiceOptions / projectCgShow).
// ─────────────────────────────────────────────────────────────

/** Project a halt's choice step `options` to the `ChoiceOptionView[]` PlayerStage
 *  renders (mirrors the subset the production choice panel reads). */
function projectChoiceOptions(step: unknown, locale: string): ChoiceOptionView[] {
  const raw = (step as { options?: unknown }).options;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((opt, i) => {
    const o = (opt ?? {}) as Record<string, unknown>;
    const mode = o.mode === "brave" || o.mode === "safe" ? (o.mode as "brave" | "safe") : undefined;
    const check =
      o.check && typeof o.check === "object"
        ? {
            attr: String((o.check as { attr?: unknown }).attr ?? ""),
            dc: Number((o.check as { dc?: unknown }).dc ?? 0),
          }
        : undefined;
    return {
      id: str(o.id) ?? `opt${i}`,
      text: str(o.text) ?? str(o.label) ?? str(o.dialogue) ?? `${locale === "en" ? "Option" : "选项"} ${i + 1}`,
      ...(mode ? { mode } : {}),
      ...(check ? { check } : {}),
    } satisfies ChoiceOptionView;
  });
}

/** Project a cg_show halt step to the overlay decision. */
function projectCgShow(step: unknown): PlayerDecision {
  const s = (step ?? {}) as Record<string, unknown>;
  return {
    kind: "cg_show",
    url: typeof s.url === "string" ? s.url : undefined,
    // Prefer assetName (what enrichNode stamps) so 跳到镜头 matches a cg whose id
    // lives in `background`; fall back to the raw `name` field.
    name: typeof s.assetName === "string" ? s.assetName : typeof s.name === "string" ? s.name : undefined,
  };
}

// ─────────────────────────────────────────────────────────────
// Internal beat-advance recipe — VERBATIM mirror of the ls-preview store's
// computeAdvanceBeat: advance one beat, dispatch on halt kind, auto-skip
// minigame decisions (PlayerStage has no minigame support).
// ─────────────────────────────────────────────────────────────

interface BeatResult {
  readonly stage: StageState;
  readonly decision: PlayerDecision | null;
  readonly decisionCursor: CursorPath | null;
  readonly ended: boolean;
}

const EMPTY_BEAT: BeatResult = {
  stage: EMPTY_STAGE,
  decision: null,
  decisionCursor: null,
  ended: false,
};

/**
 * Advance one beat from `startStage`, dispatch on the halt kind, and return the
 * resulting view state. Minigame decisions are auto-skipped (out of scope) by
 * resuming past them and re-advancing. Mirrors ls-preview `computeAdvanceBeat`.
 */
function computeAdvanceBeat(
  player: StepPlayer,
  ctx: ReduceCtx,
  episode: EpisodeShape,
  startStage: StageState,
  locale = "en",
): BeatResult {
  let stage = startStage;
  // Bounded loop: every iteration either returns or skips exactly one minigame
  // decision, so it cannot outrun the episode's decision count.
  let guard = 0;
  while (guard++ < 100000) {
    const r = advanceUntilHalt(player, stage, ctx);
    stage = r.stage;

    if (r.halt.kind === "line" || r.halt.kind === "new-char") {
      return { stage, decision: null, decisionCursor: null, ended: false };
    }
    if (r.halt.kind === "end") {
      return { stage, decision: null, decisionCursor: null, ended: true };
    }
    // decision
    const decisionType = r.halt.decisionType;
    if (decisionType === "choice") {
      return {
        stage,
        decision: { kind: "choice", options: projectChoiceOptions(r.halt.step, locale) },
        decisionCursor: r.halt.cursor,
        ended: false,
      };
    }
    if (decisionType === "cg_show") {
      return {
        stage,
        decision: projectCgShow(r.halt.step),
        decisionCursor: r.halt.cursor,
        ended: false,
      };
    }
    // minigame (out of scope) — resume past it and keep walking (no beat spent).
    const nxt = resumeAfterDecisionCursor(episode.steps, r.halt.cursor);
    if (!nxt) {
      return { stage, decision: null, decisionCursor: null, ended: true };
    }
    player.jumpTo(nxt);
  }
  // Safety valve — should be unreachable.
  return { stage, decision: null, decisionCursor: null, ended: false };
}

/**
 * PreviewController — drives BOTH the new slot `StageState` (the source of truth
 * for the PlayerStage renderer) AND the legacy text `BeatView`, off ONE
 * long-lived `StepPlayer` + `ReduceCtx`, exactly like the ls-preview store.
 *
 * The first beat is revealed in the constructor (so the stage isn't blank),
 * matching the production player + ls-preview store's `restart()`.
 */
export interface PreviewPassageTarget {
  readonly text: string;
  readonly speaker?: string;
  /** Zero-based occurrence in compiled passage order, including choice branches. */
  readonly occurrence?: number;
}

class PassagePlayer extends StepPlayer {
  lastPayload: unknown;
  override advance(): ReturnType<StepPlayer["advance"]> {
    const frame = super.advance();
    this.lastPayload = frame.payload;
    return frame;
  }
}

interface PassageMatch { node: unknown; choices: Map<unknown, string>; conditions: Map<unknown, string>; }

function findPassage(steps: unknown[], target: PreviewPassageTarget): PassageMatch | undefined {
  let occurrence = target.occurrence ?? 0;
  let found: PassageMatch | undefined;
  const walk = (nodes: unknown[], choices: Map<unknown, string>, conditions: Map<unknown, string>) => {
    for (const raw of nodes) {
      if (found) return;
      if (Array.isArray(raw)) { walk(raw, choices, conditions); continue; }
      if (!raw || typeof raw !== "object") continue;
      const node = raw as Record<string, unknown>;
      const speaker = node.type === "you" ? "YOU" : node.type === "narrator" ? "NARRATOR" : str(node.character) ?? str(node.speaker);
      if (["dialogue", "narrator", "you"].includes(String(node.type)) && node.text === target.text && (!target.speaker || speaker?.toLowerCase() === target.speaker.toLowerCase())) {
        if (occurrence-- === 0) { found = { node: raw, choices, conditions }; return; }
      }
      if (node.type === "choice" && Array.isArray(node.options)) {
        for (const option of node.options) {
          if (!option || typeof option !== "object") continue;
          const opt = option as Record<string, unknown>;
          if (typeof opt.id === "string" && Array.isArray(opt.steps)) walk(opt.steps, new Map([...choices, [raw, opt.id]]), conditions);
        }
      }
      for (const key of ["steps", "then", "else"]) if (Array.isArray(node[key])) walk(node[key] as unknown[], choices, key === "then" || key === "else" ? new Map([...conditions, [raw, key]]) : conditions);
    }
  };
  walk(steps, new Map(), new Map());
  return found;
}

/** Editor-only branch resolution: the author selected this branch explicitly.
 * No flags, checks or relationship values are evaluated or mutated here. */
function resolvePassageConditions(nodes: unknown[], match: PassageMatch): unknown[] {
  return nodes.flatMap((raw): unknown[] => {
    if (Array.isArray(raw)) return [resolvePassageConditions(raw, match)];
    if (!raw || typeof raw !== "object") return [raw];
    const node = raw as Record<string, unknown>;
    const branch = match.conditions.get(raw);
    if (branch && Array.isArray(node[branch])) return resolvePassageConditions(node[branch] as unknown[], match);
    if (node.type === "choice" && Array.isArray(node.options)) {
      const copy = { ...node, options: node.options.map((rawOption: unknown) => {
        if (!rawOption || typeof rawOption !== "object") return rawOption;
        const option = rawOption as Record<string, unknown>;
        return Array.isArray(option.steps) ? { ...option, steps: resolvePassageConditions(option.steps, match) } : option;
      }) };
      const chosen = match.choices.get(raw);
      if (chosen) match.choices.set(copy, chosen);
      return [copy];
    }
    return [raw];
  });
}

export class PreviewController {
  private player: PassagePlayer;
  private readonly ctx: ReduceCtx;
  private episode: EpisodeShape;
  private readonly originalEpisode: EpisodeShape;

  // ── slot-stage path (new source of truth) ──
  private current: BeatResult;

  // ── legacy text path (back-compat) ──
  private currentBeat: BeatView;
  private legacyDecisionCursor: CursorPath | null = null;

  constructor(output: CompiledLike | null | undefined, private readonly locale = "en") {
    this.episode = toEpisode(output);
    this.originalEpisode = this.episode;
    // lunaria-web has no protagonist concept yet → null (forcedSlotFor keeps the
    // script's normalized position while null). ONE ctx for the whole replay so
    // epoch counters stay monotonic — mirror the ls-preview store exactly.
    this.ctx = createReduceCtx(null);
    this.player = new PassagePlayer(this.episode);

    if (this.episode.steps.length === 0) {
      // No steps → immediate end (both paths agree on this).
      this.current = { ...EMPTY_BEAT, ended: true };
      this.currentBeat = { kind: "end" };
      return;
    }
    this.current = computeAdvanceBeat(this.player, this.ctx, this.episode, EMPTY_STAGE, this.locale);
    this.currentBeat = this.beatViewOf(this.current);
    this.legacyDecisionCursor = this.current.decisionCursor;
  }

  /** Reconstruct the frame at an author's passage, including its chosen branch.
   * Failed lookup leaves the running preview intact. This replays visuals; it
   * explicitly opens condition branches containing the target, without evaluating state. */
  seekToPassage(target: PreviewPassageTarget): boolean {
    const match = findPassage(this.originalEpisode.steps, target);
    if (!match) return false;
    const episode = { ...this.originalEpisode, steps: resolvePassageConditions(this.originalEpisode.steps, match) };
    const player = new PassagePlayer(episode);
    let beat = computeAdvanceBeat(player, this.ctx, episode, EMPTY_STAGE, this.locale);
    while (!beat.ended) {
      const payload = player.lastPayload;
      if (payload === match.node || (Array.isArray(payload) && payload.includes(match.node))) {
        this.player = player;
        this.episode = episode;
        this.current = beat;
        this.currentBeat = this.beatViewOf(beat);
        this.legacyDecisionCursor = beat.decisionCursor;
        return true;
      }
      if (beat.decision && beat.decisionCursor) {
        if (beat.decision.kind === "choice") {
          const option = match.choices.get(payload) ?? beat.decision.options[0]?.id;
          if (!option) return false;
          player.jumpTo(optionEntryCursor(beat.decisionCursor, option));
        } else {
          const next = resumeAfterDecisionCursor(episode.steps, beat.decisionCursor);
          if (!next) return false;
          player.jumpTo(next);
        }
      }
      beat = computeAdvanceBeat(player, this.ctx, episode, beat.stage, this.locale);
    }
    return false;
  }

  // ── slot-stage accessors (the new render contract) ──

  /** The current slot `StageState` PlayerStage renders. */
  stage(): StageState {
    return this.current.stage;
  }

  /** The active `PlayerDecision` (choice / cg_show), or null. minigame halts are
   *  auto-skipped so they never surface here. */
  decision(): PlayerDecision | null {
    return this.current.decision;
  }

  isDone(): boolean {
    return this.current.ended;
  }

  atDecision(): boolean {
    return this.current.decision !== null;
  }

  /** Advance one beat. No-op at a decision (choose / cgContinue first) or at the
   *  end. Returns the legacy BeatView for callers that still read it. */
  next(): BeatView {
    if (this.current.ended || this.current.decision) {
      return this.currentBeat;
    }
    this.current = computeAdvanceBeat(this.player, this.ctx, this.episode, this.current.stage, this.locale);
    this.currentBeat = this.beatViewOf(this.current);
    this.legacyDecisionCursor = this.current.decisionCursor;
    return this.currentBeat;
  }

  /** Descend into a chosen option's steps (same path semantics as the IDE),
   *  then advance to the next halt. */
  choose(optionId: string): BeatView {
    if (this.current.decision?.kind !== "choice" || !this.current.decisionCursor) {
      return this.currentBeat;
    }
    this.player.jumpTo(optionEntryCursor(this.current.decisionCursor, optionId));
    this.current = computeAdvanceBeat(this.player, this.ctx, this.episode, this.current.stage, this.locale);
    this.currentBeat = this.beatViewOf(this.current);
    this.legacyDecisionCursor = this.current.decisionCursor;
    return this.currentBeat;
  }

  /** Resume past a cg_show overlay (continue button), then advance. */
  cgContinue(): BeatView {
    if (this.current.decision?.kind !== "cg_show" || !this.current.decisionCursor) {
      return this.currentBeat;
    }
    const nxt = resumeAfterDecisionCursor(this.episode.steps, this.current.decisionCursor);
    if (!nxt) {
      this.current = { stage: this.current.stage, decision: null, decisionCursor: null, ended: true };
      this.currentBeat = { kind: "end" };
      this.legacyDecisionCursor = null;
      return this.currentBeat;
    }
    this.player.jumpTo(nxt);
    this.current = computeAdvanceBeat(this.player, this.ctx, this.episode, this.current.stage, this.locale);
    this.currentBeat = this.beatViewOf(this.current);
    this.legacyDecisionCursor = this.current.decisionCursor;
    return this.currentBeat;
  }

  // ── legacy text accessor (back-compat) ──

  beat(): BeatView {
    return this.currentBeat;
  }

  /** Derive the legacy text BeatView from a slot-stage beat result so the old
   *  `beat()` / `next()` return shape keeps working without a second walk. */
  private beatViewOf(r: BeatResult): BeatView {
    if (r.ended) return { kind: "end" };
    if (r.decision?.kind === "choice") {
      return {
        kind: "decision",
        options: r.decision.options.map((o) => ({ id: o.id, label: o.text })),
      };
    }
    if (r.decision?.kind === "cg_show") {
      // A cg overlay has no text line; surface it as a stage note.
      return { kind: "stage", note: `${this.locale === "en" ? "Shot" : "镜头"} · ${r.decision.name ?? ""}` };
    }
    const line = r.stage.line;
    if (line?.kind === "dialogue") {
      return { kind: "line", speaker: line.speaker, text: line.text };
    }
    if (line?.kind === "you") {
      return { kind: "line", speaker: this.locale === "en" ? "You" : "你", text: line.text };
    }
    if (line?.kind === "narrator") {
      return { kind: "narration", text: line.text };
    }
    if (line?.kind === "phone") {
      return { kind: "stage", note: `${this.locale === "en" ? "Message" : "短信"} · ${line.contact}` };
    }
    // A halt with no line (e.g. a new-char entrance beat) renders as a stage beat.
    return { kind: "stage", note: "stage" };
  }
}

// Re-export the renderer contract types so consumers (PreviewPane / App) import
// from one place.
export type { ChoiceOptionView, PlayerDecision, StageState } from "@lunaverse-ide/shared/stage-player";
